import { ofType } from 'redux-observable';
import { from, Observable, EMPTY } from 'rxjs';
import {
    getPlayerPromotionsDataSuccess,
    OLDplayerReportSuccess,
    playerReportSuccess,
    PlayerReportTypes,
    promotionDataSuccess,
} from '../../actions/backoffice/player-report-actions';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { dateFormatter, getLoginRequestHeader, localStorageHelper } from '../../../utils';
import { of } from 'rxjs';
import { IAction } from '../../../helpers/interfaces';
import { apiError } from '../../actions/user-actions';
import axios from 'axios';
import { SERVER_URL } from '../../../utils/config';
import { StateObservable } from 'redux-observable';

export const playerReportEpic = (action$: Observable<IAction>, state$: StateObservable<any>) =>
    action$.pipe(
        ofType(PlayerReportTypes.PLAYER_REPORT_REQUEST),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const { playerId } = action.payload || {};
            
            if (!playerId || playerId === 'null' || playerId === null) {
                return EMPTY;
            }
            
            const validPlayerId = String(playerId);
            
            const filters = state.playerReportReducer?.filters || {
                roundId: null,
                fromDate: null,
                toDate: null,
                roundTypes: ['regular', 'buy', 'free-round', 'awarded-feature'],
                pageSize: 20,
                currentPage: 1,
                sortBy: 'startId',
                sortOrder: 'DESCENDING' as const,
            };
            
            const currencyId = state.playerReportReducer?.currencyId;
            
            const headers = getLoginRequestHeader();
            
            const requestBody = {
                playerId: validPlayerId,
                ...(filters.roundId && filters.roundId.trim() && { roundId: filters.roundId.trim() }),
                ...(currencyId && { currencyId }),
                roundTypes: filters.roundTypes || localStorageHelper.getChainedValue('user.reports.roundTypes') || ['regular', 'buy', 'free-round', 'awarded-feature'],
                pageSize: filters.pageSize,
                currentPage: filters.currentPage,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder
            };

            if (!filters.roundId && filters.fromDate && filters.toDate) {
                const isToSendHours = localStorageHelper.getChainedValue('user.reports.isToSendHours');
                const fromDateStr = isToSendHours
                    ? dateFormatter.getFormattedStartDateWithHours(filters.fromDate.toDate())
                    : dateFormatter.getFormattedStartDate(filters.fromDate.toDate());
                const toDateStr = isToSendHours
                    ? dateFormatter.getFormattedEndDateWithHours(filters.toDate.toDate())
                    : dateFormatter.getFormattedEndDate(filters.toDate.toDate());

                requestBody.start = fromDateStr;
                requestBody.end = toDateStr;
                
                localStorageHelper.setChainedValue('user.reports.isToSendHours', false);
            }
            
            const fromPromise = from(
                axios.post(
                    `${SERVER_URL}/game-history/list-with-round-types/pageable`,
                    requestBody,
                    { headers },
                ),
            );

            return fromPromise.pipe(
                map((response) => {
                    const content = response.data.content || response.data;
                    const pagination = response.data.pagination;
                    const responseExtraInfo = response.data.extraInfo || {};
                    const extraInfo = {
                        playerCurrencyId: responseExtraInfo.playerCurrencyId || currencyId,
                        internalPlayerId: responseExtraInfo.internalPlayerId || playerId,
                        pagination: pagination ? {
                            total: pagination.totalRecords,
                            currentPage: pagination.currentPage,
                            totalPages: pagination.totalPages
                        } : null
                    };
                    return playerReportSuccess({ content, extraInfo });
                }),
                catchError((error) => of(apiError(error, PlayerReportTypes.PLAYER_REPORT_REQUEST))),
            );
        }),
    );

export const OLDplayerReportEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PlayerReportTypes.OLD_PLAYER_REPORT_REQUEST),
        mergeMap((action) => {
            const { playerId, login, fromDate, toDate } = action.payload;
            const isToSendHours = localStorageHelper.getChainedValue('user.reports.isToSendHours');
            const fromDateStr = isToSendHours
                ? dateFormatter.getFormattedStartDateWithHours(fromDate)
                : dateFormatter.getFormattedStartDate(fromDate);
            const toDateStr = isToSendHours
                ? dateFormatter.getFormattedEndDateWithHours(toDate)
                : dateFormatter.getFormattedEndDate(toDate);

            localStorageHelper.setChainedValue('user.reports.isToSendHours', false);

            const headers = getLoginRequestHeader();
            const fromPromise = from(
                axios.get(
                    `${SERVER_URL}/game-history/list?${
                        login ? `externalPlayerId=${login}` : `playerId=${playerId}`
                    }&start=${fromDateStr}&end=${toDateStr}`,
                    { headers },
                ),
            );

            return fromPromise.pipe(
                map((response) => OLDplayerReportSuccess(response.data)),
                catchError((error) =>
                    of(apiError(error, PlayerReportTypes.OLD_PLAYER_REPORT_REQUEST)),
                ),
            );
        }),
    );

export const promotionDataEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PlayerReportTypes.PROMOTION_DATA_REQUEST),
        mergeMap((action) => {
            const { playerId, gameSessionId, promotionId } = action.payload;

            const headers = getLoginRequestHeader();
            const fromPromise = from(
                axios.get(
                    `${SERVER_URL}/game-history/promotion-detail?playerId=${playerId}&gameSessionId=${gameSessionId}&promotionId=${promotionId}`,
                    { headers },
                ),
            );

            return fromPromise.pipe(
                map((response) => promotionDataSuccess(response.data)),
                catchError((error) =>
                    of(apiError(error, PlayerReportTypes.PROMOTION_DATA_REQUEST)),
                ),
            );
        }),
    );

export const playerCampaignsData = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(PlayerReportTypes.PLAYER_CAMPAIGNS_DATA_REQUEST),
        mergeMap((action) => {
            const { fromDate, toDate, login } = action.payload;

            const headers = getLoginRequestHeader();
            const fromPromise = from(
                axios.get(
                    `${SERVER_URL}/api/v1/brand/current/campaign-participant/${login}/summary?startDate=${dateFormatter.getFormattedStartDate(
                        fromDate,
                    )}&endDate=${dateFormatter.getFormattedEndDate(toDate)}`,
                    { headers },
                ),
            );

            return fromPromise.pipe(
                map((response) => getPlayerPromotionsDataSuccess(response.data)),
                catchError((error) =>
                    of(apiError(error, PlayerReportTypes.PLAYER_CAMPAIGNS_DATA_REQUEST)),
                ),
            );
        }),
    );
